import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { SYNC_STATUS_QUERY } from '../QueryKeys';
import {
  EProductLibraryStatus,
  getProductLibrarySyncStatus,
  IStatus,
} from '../../api/getProductLibrarySyncStatus/getProductLibrarySyncStatus';
import { useState } from 'react';
import { triggerProductSync } from 'src/api/triggerProductSync/triggerProductSync';

export interface IProductLibraryStatus extends IStatus {
  callCount: number;
}

interface UseProductLibraryStatusOptions {
  onSyncStatusUpdate?: (data: IProductLibraryStatus) => void;
  queryOptions?: UseQueryOptions<IProductLibraryStatus>;
  enabled?: boolean;
}

export const useProductLibraryStatus = ({
  onSyncStatusUpdate,
  queryOptions,
  enabled = true,
}: UseProductLibraryStatusOptions = {}) => {
  const [callCount, setCallCount] = useState<number>(1);
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const syncQueryKey = SYNC_STATUS_QUERY;

  const mutation = useMutation({
    mutationFn: async (instanceId?: string) => {
      return triggerProductSync(true, instanceId);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: syncQueryKey });

      // Skipped with nothing cached: a status object built from this one field alone would read as
      // a complete IProductLibraryStatus to every consumer. onSettled refetches the real one anyway.
      queryClient.setQueryData(syncQueryKey, (old?: IProductLibraryStatus) =>
        old ? { ...old, sync_status: EProductLibraryStatus.PENDING } : old,
      );
      showSnackbar({
        status: SnackbarStatus.PROGRESS,
        message: 'Product library sync started',
      });
    },
    onError: (error: unknown) => {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: error instanceof Error ? error.message : 'Failed to trigger sync',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: syncQueryKey });
    },
  });

  const query = useQuery({
    enabled,
    queryKey: syncQueryKey,
    queryFn: async () => {
      const status = await getProductLibrarySyncStatus();
      return {
        callCount,
        ...status,
      };
    },
    onError: () =>
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'Unable to fetch product library sync status' }),
    refetchInterval: (data) => (data?.sync_status === EProductLibraryStatus.PENDING ? 10 * 1000 : false),
    onSuccess: (data) => {
      setCallCount((prev) => prev + 1);
      if (onSyncStatusUpdate) onSyncStatusUpdate(data);
    },
    ...queryOptions,
  });

  return {
    isSyncInProgress: query.data?.sync_status === EProductLibraryStatus.PENDING,
    triggerSync: mutation.mutate,
    syncInfo: query.data,
    // The sync's event log id, which is also the ingest service's event id.
    eventId: query.data?.sync_id,
  };
};
